import { useEffect } from 'react';
import type Konva from 'konva';

export function useCanvasUrlShapeFocus(input: {
  nodes: Array<{ id: string; x: number; y: number; w?: number; h?: number; width?: number; height?: number }>;
  stageRef: { current: Konva.Stage | null };
  hasFocusedFromUrlRef: { current: boolean };
  setStage: (partial: { scale?: number; offsetX?: number; offsetY?: number }) => void;
  setSelected: (next: Set<string>) => void;
  setUserSelection?: (ids: string[]) => void;
}): void {
  const { nodes, stageRef, hasFocusedFromUrlRef, setStage, setSelected, setUserSelection } = input;

  // If URL has ?shape=<shapeId> (or legacy /s/<shapeId>), focus that shape once nodes are ready
  useEffect(() => {
    const url = new URL(window.location.href);
    const qp = url.searchParams.get('shape');
    const mp = window.location.pathname.match(/\/s\/([\w-]+)/);
    const shapeId = qp || (mp ? mp[1] : null);
    if (!shapeId || hasFocusedFromUrlRef.current) return;
    let attempts = 0;
    const tryFocus = () => {
      attempts++;
      const target = nodes.find((n) => n.id === shapeId);
      if (target && stageRef.current) {
        const stage = stageRef.current;
        const scale = stage.scaleX();
        const view = stage.container().getBoundingClientRect();
        const cx = target.x + (target.w || (target as any).width || 60) / 2;
        const cy = target.y + (target.h || (target as any).height || 60) / 2;
        const nextX = view.width / 2 - cx * scale;
        const nextY = view.height / 2 - cy * scale;
        stage.position({ x: nextX, y: nextY });
        setStage({ offsetX: nextX, offsetY: nextY });
        setSelected(new Set([shapeId]));
        setUserSelection?.([shapeId]);
        hasFocusedFromUrlRef.current = true;
        return;
      }
      if (attempts < 120) requestAnimationFrame(tryFocus);
    };
    requestAnimationFrame(tryFocus);
  }, [nodes, hasFocusedFromUrlRef, setSelected, setStage, setUserSelection, stageRef]);
}

